import { useEffect, useState } from "preact/hooks"
import { api } from "../lib/api"
import { thumbUrl } from "../lib/cloudinary"
import { artDateGroupLabel, groupByLabel } from "../lib/time"
import type { Artwork, BoxDetail } from "../lib/types"
import { Spinner } from "./Spinner"

interface BoxViewProps {
	boxId: number
	cloudName: string
}

export function BoxView({ boxId, cloudName }: BoxViewProps) {
	const [detail, setDetail] = useState<BoxDetail | null>(null)
	const [artworks, setArtworks] = useState<Artwork[] | null>(null)
	const [error, setError] = useState("")

	useEffect(() => {
		setDetail(null)
		setArtworks(null)
		setError("")
		Promise.all([api.getBox(boxId), api.listArtworks(boxId)])
			.then(([d, a]) => {
				setDetail(d)
				setArtworks(a)
			})
			.catch((err) =>
				setError(err instanceof Error ? err.message : "Failed to load box"),
			)
	}, [boxId])

	if (error) {
		return (
			<div class="container py-4">
				<div class="alert alert-danger">{error}</div>
			</div>
		)
	}
	if (!detail || !artworks) {
		return (
			<div class="container py-4">
				<Spinner />
			</div>
		)
	}

	const isOwner = detail.role === "owner"
	const groups = groupByLabel(artworks, (a) => artDateGroupLabel(a.date))
	// The grid groups by made-date, so a piece photographed today but dated
	// to the past lands in an old group; badge off created_at instead so a
	// returning viewer can still spot what's new.
	const newCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000

	return (
		<div class="container py-4 pb-5">
			<div class="d-flex align-items-center justify-content-between mb-3 gap-2">
				<div>
					<h1 class="h4 mb-0 text-break">{detail.box.name}</h1>
					<div class="small text-body-secondary">
						{artworks.length} {artworks.length === 1 ? "piece" : "pieces"}
					</div>
				</div>
				{isOwner && (
					<div class="d-flex gap-2">
						{/* Desktop (md+): regular button in the header, like Home's
							"New box". Smaller screens use the floating + button below. */}
						<a
							href={`/boxes/${boxId}/add`}
							class="btn btn-primary d-none d-md-inline-flex align-items-center gap-1"
						>
							<i class="bi bi-plus-lg" aria-hidden="true" />
							<span>New art</span>
						</a>
						<a
							href={`/boxes/${boxId}/members`}
							class="btn btn-outline-secondary"
						>
							<i class="bi bi-people" aria-hidden="true" />
							<span class="d-none d-sm-inline">Members</span>
						</a>
					</div>
				)}
			</div>

			{artworks.length === 0 ? (
				<p class="text-body-secondary">
					No art yet.{isOwner && " Add the first piece!"}
				</p>
			) : (
				groups.map((group) => (
					<div class="mb-4" key={group.label}>
						<h2 class="h6 text-body-secondary mb-2">{group.label}</h2>
						<div class="row row-cols-3 row-cols-sm-4 row-cols-md-5 g-2">
							{group.items.map((art) => {
								const photo = art.assets.find((a) => a.kind === "photo")
								const recordingAsset = art.assets.find(
									(a) => a.kind === "recording",
								)
								const isVideo = recordingAsset
									? recordingAsset.width > 0
									: false
								const isNew = new Date(art.created_at).getTime() >= newCutoff
								return (
									<div class="col" key={art.id}>
										<a
											href={`/artworks/${art.id}`}
											class="d-block text-decoration-none position-relative"
										>
											<div class="ratio ratio-1x1 bg-body-secondary rounded overflow-hidden">
												{photo && cloudName ? (
													<img
														src={thumbUrl(
															cloudName,
															photo.cloudinary_public_id,
														)}
														alt={art.title || ""}
														class="w-100 h-100 object-fit-cover"
													/>
												) : (
													<div class="d-flex align-items-center justify-content-center">
														<i
															class="bi bi-image fs-2 text-body-secondary"
															aria-hidden="true"
														/>
													</div>
												)}
											</div>
											{isNew && (
												<span
													class="position-absolute top-0 start-0 m-1 badge rounded-pill text-bg-accent"
													title="Added in the last 7 days"
												>
													New
												</span>
											)}
											{recordingAsset && (
												<span
													class="position-absolute bottom-0 end-0 m-1 badge rounded-pill text-bg-dark"
													title={isVideo ? "Has a video" : "Has a recording"}
												>
													{isVideo ? "🎬" : "🎙"}
												</span>
											)}
										</a>
									</div>
								)
							})}
						</div>
					</div>
				))
			)}

			{isOwner && (
				<a
					href={`/boxes/${boxId}/add`}
					class="btn btn-primary btn-lg rounded-circle shadow position-fixed d-flex d-md-none align-items-center justify-content-center"
					style={{
						width: "60px",
						height: "60px",
						// iOS safe areas (notch/home-indicator): fall back to the
						// plain offset when env() isn't supported.
						bottom: "calc(1.5rem + env(safe-area-inset-bottom))",
						right: "calc(1.5rem + env(safe-area-inset-right))",
					}}
					aria-label="Add art"
					title="Add art"
				>
					<i class="bi bi-plus-lg fs-4" aria-hidden="true" />
				</a>
			)}
		</div>
	)
}
